import { useState, useEffect, useCallback } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Loader2, Warehouse, LogOut, Lock, Search, Minus, Package, AlertTriangle, History, Car, Check,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import { api } from "../lib/api";

export default function WarehousePortal() {
  const { token } = useParams();
  const [info, setInfo] = useState(null);
  const [me, setMe] = useState(null);
  const [checking, setChecking] = useState(true);
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const [tab, setTab] = useState("withdraw");
  const [parts, setParts] = useState(null);
  const [search, setSearch] = useState("");
  const [vehicles, setVehicles] = useState([]);
  const [consumptions, setConsumptions] = useState(null);
  const [consume, setConsume] = useState(null); // part being withdrawn
  const [qty, setQty] = useState("");
  const [veh, setVeh] = useState("");
  const [saving, setSaving] = useState(false);

  const check = useCallback(async () => {
    try { setMe(await api.warehouseMe()); } catch { setMe(null); }
    finally { setChecking(false); }
  }, []);

  useEffect(() => {
    api.publicWarehouse(token).then(setInfo).catch(() => setInfo({ name: "المستودع", org_name: "" }));
    check();
  }, [token, check]);

  const loadParts = useCallback(async () => {
    try { setParts((await api.warehouseParts(search)).items || []); } catch { toast.error("تعذّر تحميل القطع"); }
  }, [search]);
  const loadVehicles = useCallback(async () => {
    try { setVehicles((await api.warehouseVehicles()).vehicles || []); } catch { /* */ }
  }, []);
  const loadConsumptions = useCallback(async () => {
    try { setConsumptions((await api.warehouseConsumptions()).items || []); } catch { /* */ }
  }, []);

  useEffect(() => { if (me) { loadParts(); loadVehicles(); } }, [me, loadParts, loadVehicles]);
  useEffect(() => { if (me && tab === "history") loadConsumptions(); }, [me, tab, loadConsumptions]);

  const login = async () => {
    if (!password) { toast.warning("أدخل كلمة المرور"); return; }
    setBusy(true);
    try { await api.warehouseLogin(token, password); await check(); toast.success("مرحباً بك"); }
    catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الدخول"); }
    finally { setBusy(false); }
  };
  const logout = async () => { try { await api.warehouseLogout(); } catch { /* */ } setMe(null); setPassword(""); };

  const openConsume = (p) => { setConsume(p); setQty(""); setVeh(""); };
  const doConsume = async () => {
    const n = Number(qty);
    if (!n || n <= 0) { toast.warning("أدخل كمية صحيحة"); return; }
    setSaving(true);
    try {
      await api.warehouseConsume(consume.id, { quantity: n, vehicle_number: veh });
      toast.success("تم صرف القطعة");
      setConsume(null); loadParts();
    } catch (e) { toast.error(e?.response?.data?.detail || "تعذّر الصرف"); }
    finally { setSaving(false); }
  };

  if (checking) return <div className="min-h-screen grid place-items-center bg-slate-50"><Loader2 className="h-7 w-7 animate-spin text-blue-600" /></div>;

  if (!me) {
    return (
      <div dir="rtl" className="min-h-screen grid place-items-center bg-gradient-to-br from-slate-100 to-blue-50 p-4 font-body">
        <div className="w-full max-w-sm bg-white rounded-2xl shadow-xl border border-slate-100 p-6 space-y-5" data-testid="wh-portal-login">
          <div className="flex flex-col items-center gap-3 text-center">
            <div className="h-14 w-14 rounded-2xl bg-blue-600 grid place-items-center text-white"><Warehouse className="h-7 w-7" /></div>
            <div>
              <h1 className="font-heading text-xl font-bold text-slate-800">{info?.name || "بوابة المستودع"}</h1>
              <p className="text-xs text-slate-400 mt-0.5">{info?.city || info?.org_name || "صرف قطع الغيار"}</p>
            </div>
          </div>
          <div className="space-y-3">
            <div className="space-y-1">
              <label className="text-xs font-semibold text-slate-500">كلمة المرور</label>
              <Input data-testid="wh-portal-password" dir="ltr" type="password" value={password}
                onChange={(e) => setPassword(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") login(); }} placeholder="••••••••" />
            </div>
            <Button onClick={login} disabled={busy} className="w-full gap-1.5" data-testid="wh-portal-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />} دخول
            </Button>
          </div>
          <p className="text-[11px] text-slate-400 text-center">بوابة مخصّصة لصرف قطع غيار هذا المستودع. للحصول على كلمة المرور تواصل مع الإدارة.</p>
        </div>
      </div>
    );
  }

  const list = parts || [];

  return (
    <div dir="rtl" className="min-h-screen bg-slate-50 font-body">
      <header className="sticky top-0 z-20 bg-white border-b border-slate-200 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <Warehouse className="h-6 w-6 text-blue-600" />
          <div className="leading-tight">
            <p className="font-heading font-bold text-sm text-slate-800">{me.name}</p>
            <p className="text-[10px] text-slate-400">{me.city || "بوابة المستودع"} — سحب فقط</p>
          </div>
        </div>
        <Button variant="ghost" size="sm" onClick={logout} data-testid="wh-portal-logout" className="gap-1.5 text-slate-500">
          <LogOut className="h-4 w-4" /> خروج
        </Button>
      </header>

      <main className="max-w-3xl mx-auto p-3 sm:p-5 space-y-4">
        <Tabs value={tab} onValueChange={setTab} dir="rtl">
          <TabsList data-testid="wh-portal-tabs">
            <TabsTrigger value="withdraw" data-testid="wh-tab-withdraw">صرف القطع</TabsTrigger>
            <TabsTrigger value="history" data-testid="wh-tab-history">سجل الصرف</TabsTrigger>
          </TabsList>
        </Tabs>

        {tab === "withdraw" ? (
          <>
            <div className="relative">
              <Search className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input data-testid="wh-search" placeholder="بحث سريع بالاسم أو الرقم..." value={search}
                onChange={(e) => setSearch(e.target.value)} className="pr-9" />
            </div>
            {!parts ? (
              <div className="py-12 text-center text-slate-300"><Loader2 className="mx-auto h-6 w-6 animate-spin" /></div>
            ) : list.length === 0 ? (
              <div className="py-12 text-center text-slate-400" data-testid="wh-parts-empty">
                <Package className="h-10 w-10 mx-auto mb-2 opacity-40" /> لا توجد قطع في هذا المستودع
              </div>
            ) : (
              <div className="space-y-2" data-testid="wh-parts">
                {list.map((p) => (
                  <div key={p.id} data-testid={`wh-part-${p.id}`} className="rounded-2xl border border-slate-200 bg-white p-3 flex items-center justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-semibold text-slate-800 truncate">{p.name}</p>
                      <p className="text-[11px] text-slate-400 font-mono-nums" dir="ltr">SKU: {p.sku}</p>
                    </div>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className={`inline-flex items-center gap-1 font-mono-nums font-bold ${p.low_stock ? "text-amber-600" : "text-slate-700"}`}>
                        {p.quantity}{p.low_stock && <AlertTriangle className="h-3.5 w-3.5" />}
                      </span>
                      <Button size="sm" data-testid={`wh-withdraw-${p.id}`} onClick={() => openConsume(p)} disabled={Number(p.quantity) <= 0}
                        className="gap-1 bg-blue-600 hover:bg-blue-700 h-8">
                        <Minus className="h-4 w-4" /> صرف
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white" data-testid="wh-history">
            <table className="w-full min-w-[520px] text-sm text-right">
              <thead className="bg-slate-50 text-slate-500 text-xs">
                <tr>
                  <th className="px-3 py-2.5 font-semibold">التاريخ</th>
                  <th className="px-3 py-2.5 font-semibold">القطعة</th>
                  <th className="px-3 py-2.5 font-semibold">الكمية</th>
                  <th className="px-3 py-2.5 font-semibold">المركبة</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {!consumptions ? (
                  <tr><td colSpan={4} className="px-3 py-10 text-center text-slate-300"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></td></tr>
                ) : consumptions.length === 0 ? (
                  <tr><td colSpan={4} className="px-3 py-10 text-center text-slate-400">لا يوجد سجل صرف بعد.</td></tr>
                ) : consumptions.map((c) => (
                  <tr key={c.id} data-testid={`wh-cons-${c.id}`} className="hover:bg-slate-50/60">
                    <td className="px-3 py-2.5 text-slate-500 font-mono-nums" dir="ltr">{String(c.created_at).slice(0, 16).replace("T", " ")}</td>
                    <td className="px-3 py-2.5 font-semibold text-slate-800">{c.part_name}</td>
                    <td className="px-3 py-2.5 font-mono-nums font-bold text-rose-600">{c.quantity}</td>
                    <td className="px-3 py-2.5"><span className="inline-flex items-center gap-1 text-slate-700 font-mono-nums">{c.vehicle_number ? <><Car className="h-3.5 w-3.5 text-slate-400" />{c.vehicle_number}</> : "—"}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      <Dialog open={!!consume} onOpenChange={(v) => !v && setConsume(null)}>
        <DialogContent className="sm:max-w-sm font-body" dir="rtl" data-testid="wh-consume-dialog">
          <DialogHeader className="text-right">
            <DialogTitle className="font-heading text-lg">صرف — {consume?.name}</DialogTitle>
            <DialogDescription>المتوفر: {consume?.quantity}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-500">العدد المطلوب</label>
              <Input data-testid="wh-consume-qty" type="number" dir="ltr" value={qty} onChange={(e) => setQty(e.target.value)} autoFocus />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold text-slate-500">المركبة (اختياري)</label>
              <Input data-testid="wh-consume-veh" list="wh-veh-list" value={veh} onChange={(e) => setVeh(e.target.value)} placeholder="اختر أو اكتب رقم المركبة" />
              <datalist id="wh-veh-list">{vehicles.map((v) => <option key={v} value={v} />)}</datalist>
            </div>
          </div>
          <DialogFooter className="flex-row-reverse gap-2 sm:justify-start">
            <Button data-testid="wh-consume-confirm" disabled={saving} onClick={doConsume} className="bg-blue-600 hover:bg-blue-700 gap-1.5">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />} تأكيد الصرف
            </Button>
            <Button variant="outline" onClick={() => setConsume(null)}>إلغاء</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
